import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  LayoutDashboard, BookOpen, Users, DollarSign, BarChart3, Plus, Edit, Trash2,
  Loader2, Settings, GraduationCap, TrendingUp, Eye, Download, Mail, 
  ChevronRight, CheckCircle, Clock, AlertCircle, Building2, Layers, FileText
} from 'lucide-react';

const CATEGORIES = [
  "AI & Technology", "Public Health", "Pharmacology", "Medical Ethics",
  "Digital Health", "Research Methods", "Psychiatry", "Healthcare Management",
  "Genetics & Genomics", "Clinical Skills", "Surgery", "Pediatrics",
  "Data Science", "Academic Writing", "Emergency Medicine"
];

const LEVELS = ["Beginner", "Intermediate", "Advanced"];

export default function UniversityDashboard() {
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [tab, setTab] = useState('overview');
  const [courses, setCourses] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileDialog, setProfileDialog] = useState(false);
  const [courseDialog, setCourseDialog] = useState(false);
  const [moduleDialog, setModuleDialog] = useState(false);
  const [lessonDialog, setLessonDialog] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [profileForm, setProfileForm] = useState({
    university_name: '', accreditation: '', logo_url: '', institution_id: '', description: '', country: '', city: '', phone: '', website_url: ''
  });

  const [courseForm, setCourseForm] = useState({
    title: '', short_description: '', description: '', price: '', category: '', level: 'Beginner', duration: '', instructor: ''
  });

  const [moduleForm, setModuleForm] = useState({ title: '', description: '', order: 1 });
  const [lessonForm, setLessonForm] = useState({ title: '', type: 'text', content: '', video_url: '', duration_minutes: 10, order: 1 });

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (user) {
      setProfileForm({
        university_name: user.profile?.university_name || user.name || '',
        accreditation: user.profile?.accreditation || '',
        logo_url: user.profile?.logo_url || '',
        institution_id: user.profile?.institution_id || '',
        description: user.profile?.description || '',
        country: user.profile?.country || '',
        city: user.profile?.city || '',
        phone: user.profile?.phone || '',
        website_url: user.profile?.website_url || ''
      });
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [coursesRes, analyticsRes] = await Promise.all([
        api.get('/university/courses'),
        api.get('/university/analytics')
      ]);
      setCourses(coursesRes.data.courses || []);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setActionLoading(true);
    try {
      await api.put('/users/profile', profileForm);
      toast.success('Profile updated!');
      setProfileDialog(false);
    } catch { toast.error('Failed to update'); }
    finally { setActionLoading(false); }
  };

  const openCreateCourse = () => {
    // Navigate to new Course Builder
    navigate('/university/course-builder');
  };

  const openEditCourse = (course) => {
    // Navigate to Course Builder in edit mode
    navigate(`/university/course-builder/${course.course_id}`);
  };

  const saveCourse = async () => {
    if (!courseForm.title || !courseForm.description || !courseForm.category) {
      toast.error('Please fill required fields');
      return;
    }
    setActionLoading(true);
    try {
      const payload = { ...courseForm, price: parseFloat(courseForm.price) || 0 };
      if (selectedCourse) {
        await api.put(`/university/courses/${selectedCourse.course_id}`, payload);
        toast.success('Course updated!');
      } else {
        await api.post('/university/courses', payload);
        toast.success('Course created!');
      }
      setCourseDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save course');
    } finally {
      setActionLoading(false);
    }
  };

  const deleteCourse = async (courseId) => {
    if (!window.confirm('Delete this course? This action cannot be undone.')) return;
    try {
      await api.delete(`/university/courses/${courseId}`);
      toast.success('Course deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const openAddModule = (course) => {
    setSelectedCourse(course);
    setModuleForm({ title: '', description: '', order: (course.modules_count || 0) + 1 });
    setModuleDialog(true);
  };

  const saveModule = async () => {
    if (!moduleForm.title) { toast.error('Title required'); return; }
    setActionLoading(true);
    try {
      await api.post(`/university/courses/${selectedCourse.course_id}/modules`, moduleForm);
      toast.success('Module added!');
      setModuleDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add module');
    } finally {
      setActionLoading(false);
    }
  };

  const openAddLesson = (course, module) => {
    setSelectedCourse(course);
    setSelectedModule(module);
    setLessonForm({ title: '', type: 'text', content: '', video_url: '', duration_minutes: 10, order: (module.lessons_count || 0) + 1 });
    setLessonDialog(true);
  };

  const saveLesson = async () => {
    if (!lessonForm.title) { toast.error('Title required'); return; }
    setActionLoading(true);
    try {
      await api.post(`/university/modules/${selectedModule.module_id}/lessons`, lessonForm);
      toast.success('Lesson added!');
      setLessonDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add lesson');
    } finally {
      setActionLoading(false);
    }
  };

  const stats = [
    { label: 'Total Courses', value: analytics?.total_courses || 0, icon: <BookOpen className="h-5 w-5" />, color: 'bg-blue-50 text-blue-600' },
    { label: 'Total Enrolled', value: analytics?.total_enrolled || 0, icon: <Users className="h-5 w-5" />, color: 'bg-emerald-50 text-emerald-600' },
    { label: 'Revenue', value: `$${(analytics?.total_revenue || 0).toFixed(2)}`, icon: <DollarSign className="h-5 w-5" />, color: 'bg-amber-50 text-amber-600' },
    { label: 'Completion Rate', value: analytics?.total_enrolled > 0 ? `${Math.round((analytics?.total_completed || 0) / analytics.total_enrolled * 100)}%` : '0%', icon: <TrendingUp className="h-5 w-5" />, color: 'bg-purple-50 text-purple-600' },
  ];

  const isVerified = user?.verified;
  const verificationStatus = user?.verification_status || (isVerified ? 'verified' : 'pending');

  return (
    <div className="min-h-screen bg-slate-50 pt-16" data-testid="university-dashboard">
      {/* Verification Pending Banner - More Prominent */}
      {!isVerified && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <Clock className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg mb-1">Verification Pending</h3>
                <p className="text-white/90 text-sm mb-2">
                  Your institution is currently under review by our team. This typically takes 1-2 business days.
                </p>
                <div className="flex flex-wrap gap-3">
                  <div className="flex items-center gap-2 text-xs bg-white/20 rounded-full px-3 py-1">
                    <AlertCircle className="h-3.5 w-3.5" />
                    <span>Course creation is locked until verification</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs bg-white/20 rounded-full px-3 py-1">
                    <CheckCircle className="h-3.5 w-3.5" />
                    <span>You can update your profile and settings</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejected Banner */}
      {verificationStatus === 'rejected' && (
        <div className="bg-red-500 text-white px-4 py-4">
          <div className="max-w-7xl mx-auto flex items-center gap-4">
            <AlertCircle className="h-6 w-6 flex-shrink-0" />
            <div>
              <h3 className="font-bold">Verification Rejected</h3>
              <p className="text-sm text-white/90">Your institution verification was not approved. Please contact support for more information.</p>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-[#0a2942] to-[#1a5c5c] flex items-center justify-center">
              <Building2 className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                {user?.profile?.university_name || user?.name || 'University Dashboard'}
              </h1>
              <p className="text-sm text-slate-500 flex items-center gap-2">
                Institution Portal
                {isVerified ? (
                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 text-xs"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>
                ) : verificationStatus === 'rejected' ? (
                  <Badge className="bg-red-50 text-red-700 border-red-100 text-xs"><AlertCircle className="h-3 w-3 mr-1" />Rejected</Badge>
                ) : (
                  <Badge className="bg-amber-50 text-amber-700 border-amber-100 text-xs"><Clock className="h-3 w-3 mr-1" />Pending Verification</Badge>
                )}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setProfileDialog(true)} className="text-slate-600" data-testid="edit-profile-btn">
              <Settings className="h-4 w-4 mr-2" /> Institution Settings
            </Button>
            <Button 
              onClick={openCreateCourse} 
              className={isVerified ? "bg-[#0a2942] hover:bg-[#0d3a5c] text-white" : "bg-slate-300 text-slate-500 cursor-not-allowed"} 
              data-testid="create-course-btn" 
              disabled={!isVerified}
              title={!isVerified ? "Verification required to create courses" : ""}
            >
              <Plus className="h-4 w-4 mr-2" /> Create Course
            </Button>
          </div>
        </div>

        {/* Verification Info Card for Unverified */}
        {!isVerified && verificationStatus !== 'rejected' && (
          <div className="mb-8 bg-white border-2 border-amber-200 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Building2 className="h-6 w-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Complete Your Institution Profile</h3>
                <p className="text-sm text-slate-600 mb-4">
                  To expedite verification, please ensure your institution profile is complete with:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="h-4 w-4 text-amber-500" /> Institution name
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="h-4 w-4 text-amber-500" /> Accreditation details
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="h-4 w-4 text-amber-500" /> Location (country, city)
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="h-4 w-4 text-amber-500" /> Contact information
                  </div>
                </div>
                <Button onClick={() => setProfileDialog(true)} className="mt-4 bg-amber-500 hover:bg-amber-600 text-white">
                  <Settings className="h-4 w-4 mr-2" /> Complete Profile
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((s) => (
            <div key={s.label} className="bg-white border border-slate-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <div className={`h-10 w-10 rounded-lg ${s.color} flex items-center justify-center`}>{s.icon}</div>
              </div>
              <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{s.value}</p>
              <p className="text-sm text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6 bg-white border border-slate-100">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#0a2942] data-[state=active]:text-white" data-testid="tab-overview">
              <LayoutDashboard className="h-4 w-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="courses" className="data-[state=active]:bg-[#0a2942] data-[state=active]:text-white" data-testid="tab-courses">
              <BookOpen className="h-4 w-4 mr-2" /> Courses
            </TabsTrigger>
            <TabsTrigger value="students" className="data-[state=active]:bg-[#0a2942] data-[state=active]:text-white" data-testid="tab-students">
              <Users className="h-4 w-4 mr-2" /> Students
            </TabsTrigger>
            <TabsTrigger value="revenue" className="data-[state=active]:bg-[#0a2942] data-[state=active]:text-white" data-testid="tab-revenue">
              <DollarSign className="h-4 w-4 mr-2" /> Revenue
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white border border-slate-100 rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Course Performance</h3>
                </div>
                {loading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
                ) : analytics?.course_stats?.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <BookOpen className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                    <p>No courses yet. Create your first course to get started.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {analytics?.course_stats?.slice(0, 5).map((cs) => (
                      <div key={cs.course_id} className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0">
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{cs.title}</p>
                          <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                            <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{cs.enrolled} enrolled</span>
                            <span className="flex items-center gap-1"><CheckCircle className="h-3.5 w-3.5" />{cs.completed} completed</span>
                            <span className="flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" />${cs.revenue.toFixed(2)}</span>
                          </div>
                        </div>
                        <Badge className={cs.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-0' : 'bg-slate-100 text-slate-600 border-0'}>
                          {cs.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>Quick Actions</h3>
                <div className="space-y-3">
                  <Button onClick={openCreateCourse} variant="outline" className="w-full justify-start text-slate-700" disabled={!isVerified}>
                    <Plus className="h-4 w-4 mr-3 text-emerald-600" /> Create New Course
                    {!isVerified && <span className="ml-auto text-xs text-amber-600">Verification required</span>}
                  </Button>
                  <Button onClick={() => setTab('courses')} variant="outline" className="w-full justify-start text-slate-700">
                    <Layers className="h-4 w-4 mr-3 text-blue-600" /> Manage Course Content
                  </Button>
                  <Button onClick={() => setTab('students')} variant="outline" className="w-full justify-start text-slate-700">
                    <Users className="h-4 w-4 mr-3 text-purple-600" /> View Students
                  </Button>
                  <Button onClick={() => setProfileDialog(true)} variant="outline" className="w-full justify-start text-slate-700">
                    <Settings className="h-4 w-4 mr-3 text-slate-600" /> Update Profile
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses">
            <div className="bg-white border border-slate-100 rounded-xl shadow-sm">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Course Management</h3>
                <Button onClick={openCreateCourse} className="bg-[#0a2942] hover:bg-[#0d3a5c] text-white" disabled={!isVerified}>
                  <Plus className="h-4 w-4 mr-2" /> New Course
                </Button>
              </div>
              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
              ) : courses.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-4">No courses created yet</p>
                  <Button onClick={openCreateCourse} disabled={!isVerified}>Create Your First Course</Button>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {courses.map((course) => (
                    <div key={course.course_id} className="p-6" data-testid={`course-row-${course.course_id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-slate-900">{course.title}</h4>
                            <Badge className={course.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-0 text-xs' : 'bg-slate-100 text-slate-600 border-0 text-xs'}>
                              {course.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500 line-clamp-1 mb-2">{course.short_description || course.description}</p>
                          <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                            <span className="flex items-center gap-1"><GraduationCap className="h-3.5 w-3.5" />{course.enrolled_count || 0} students</span>
                            <span className="flex items-center gap-1"><Layers className="h-3.5 w-3.5" />{course.modules_count || 0} modules</span>
                            <span className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" />{course.lessons_count || 0} lessons</span>
                            <span className="flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" />${course.price}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => openAddModule(course)} data-testid={`add-module-btn-${course.course_id}`} disabled={!isVerified}>
                            <Plus className="h-3.5 w-3.5 mr-1" /> Module
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => openEditCourse(course)} disabled={!isVerified}>
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => navigate(`/courses/${course.course_id}`)}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => deleteCourse(course.course_id)} className="text-red-600 hover:text-red-700" disabled={!isVerified}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Enrolled Students</h3>
                <Button variant="outline" size="sm"><Download className="h-4 w-4 mr-2" /> Export CSV</Button>
              </div>
              {analytics?.total_enrolled === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No students enrolled yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {analytics?.course_stats?.filter(c => c.enrolled > 0).map((cs) => (
                    <div key={cs.course_id} className="border border-slate-100 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-medium text-slate-900">{cs.title}</p>
                          <p className="text-sm text-slate-500">{cs.enrolled} students enrolled</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-emerald-600">{cs.completed} completed</p>
                          <p className="text-xs text-slate-400">{cs.enrolled > 0 ? Math.round(cs.completed / cs.enrolled * 100) : 0}% completion rate</p>
                        </div>
                      </div>
                      <Progress value={cs.enrolled > 0 ? (cs.completed / cs.enrolled * 100) : 0} className="h-2" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Revenue Tab */}
          <TabsContent value="revenue">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white border border-slate-100 rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope' }}>Revenue by Course</h3>
                {analytics?.course_stats?.filter(c => c.revenue > 0).length === 0 ? (
                  <div className="text-center py-12">
                    <DollarSign className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No revenue generated yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {analytics?.course_stats?.map((cs) => (
                      <div key={cs.course_id} className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0">
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{cs.title}</p>
                          <p className="text-xs text-slate-500">{cs.enrolled} enrollments</p>
                        </div>
                        <p className="text-lg font-bold text-emerald-600">${cs.revenue.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white border border-slate-100 rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>Payout Summary</h3>
                <div className="space-y-4">
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-600">Total Revenue</span>
                    <span className="font-semibold text-slate-900">${(analytics?.total_revenue || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-slate-100">
                    <span className="text-slate-600">Platform Fee (15%)</span>
                    <span className="font-semibold text-red-600">-${((analytics?.total_revenue || 0) * 0.15).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-slate-600 font-medium">Net Payout</span>
                    <span className="font-bold text-emerald-600">${((analytics?.total_revenue || 0) * 0.85).toFixed(2)}</span>
                  </div>
                </div>
                <Button className="w-full mt-6 bg-[#0a2942] hover:bg-[#0d3a5c] text-white" disabled={!analytics?.total_revenue}>
                  Request Withdrawal
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Profile Dialog */}
      <Dialog open={profileDialog} onOpenChange={setProfileDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Institution Settings</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div><Label>Institution Name *</Label><Input value={profileForm.university_name} onChange={e => setProfileForm({...profileForm, university_name: e.target.value})} /></div>
            <div><Label>Accreditation</Label><Input value={profileForm.accreditation} onChange={e => setProfileForm({...profileForm, accreditation: e.target.value})} placeholder="e.g., NAAC A+, UGC Recognized" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>University ID</Label><Input value={profileForm.institution_id} onChange={e => setProfileForm({...profileForm, institution_id: e.target.value})} placeholder="e.g., UNI-2026-001" /></div>
              <div><Label>Logo URL</Label><Input value={profileForm.logo_url} onChange={e => setProfileForm({...profileForm, logo_url: e.target.value})} placeholder="https://..." /></div>
            </div>
            {profileForm.logo_url ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-3">Logo Preview</p>
                <img
                  src={profileForm.logo_url}
                  alt="University logo preview"
                  className="h-16 w-16 rounded-lg object-contain bg-white border border-slate-200 p-2"
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';
                  }}
                  onLoad={(event) => {
                    event.currentTarget.style.display = 'block';
                  }}
                />
              </div>
            ) : null}
            <div><Label>Description</Label><Textarea value={profileForm.description} onChange={e => setProfileForm({...profileForm, description: e.target.value})} rows={3} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Country</Label><Input value={profileForm.country} onChange={e => setProfileForm({...profileForm, country: e.target.value})} /></div>
              <div><Label>City</Label><Input value={profileForm.city} onChange={e => setProfileForm({...profileForm, city: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Phone</Label><Input value={profileForm.phone} onChange={e => setProfileForm({...profileForm, phone: e.target.value})} /></div>
              <div><Label>Website</Label><Input value={profileForm.website_url} onChange={e => setProfileForm({...profileForm, website_url: e.target.value})} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileDialog(false)}>Cancel</Button>
            <Button onClick={saveProfile} disabled={actionLoading} className="bg-[#0a2942] hover:bg-[#0d3a5c] text-white">
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Course Dialog */}
      <Dialog open={courseDialog} onOpenChange={setCourseDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>{selectedCourse ? 'Edit Course' : 'Create Course'}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div><Label>Title *</Label><Input value={courseForm.title} onChange={e => setCourseForm({...courseForm, title: e.target.value})} data-testid="course-title-input" /></div>
            <div><Label>Short Description</Label><Input value={courseForm.short_description} onChange={e => setCourseForm({...courseForm, short_description: e.target.value})} placeholder="Brief tagline" /></div>
            <div><Label>Full Description *</Label><Textarea value={courseForm.description} onChange={e => setCourseForm({...courseForm, description: e.target.value})} rows={4} data-testid="course-desc-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <Select value={courseForm.category} onValueChange={v => setCourseForm({...courseForm, category: v})}>
                  <SelectTrigger data-testid="course-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Level</Label>
                <Select value={courseForm.level} onValueChange={v => setCourseForm({...courseForm, level: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Price ($)</Label><Input type="number" value={courseForm.price} onChange={e => setCourseForm({...courseForm, price: e.target.value})} placeholder="0 for free" data-testid="course-price-input" /></div>
              <div><Label>Duration</Label><Input value={courseForm.duration} onChange={e => setCourseForm({...courseForm, duration: e.target.value})} placeholder="e.g., 8 weeks" /></div>
              <div><Label>Instructor</Label><Input value={courseForm.instructor} onChange={e => setCourseForm({...courseForm, instructor: e.target.value})} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCourseDialog(false)}>Cancel</Button>
            <Button onClick={saveCourse} disabled={actionLoading} className="bg-[#0a2942] hover:bg-[#0d3a5c] text-white" data-testid="save-course-btn">
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {selectedCourse ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Module Dialog */}
      <Dialog open={moduleDialog} onOpenChange={setModuleDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Add Module</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Module Title *</Label><Input value={moduleForm.title} onChange={e => setModuleForm({...moduleForm, title: e.target.value})} data-testid="module-title-input" /></div>
            <div><Label>Description</Label><Textarea value={moduleForm.description} onChange={e => setModuleForm({...moduleForm, description: e.target.value})} rows={2} /></div>
            <div><Label>Order</Label><Input type="number" value={moduleForm.order} onChange={e => setModuleForm({...moduleForm, order: parseInt(e.target.value) || 1})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModuleDialog(false)}>Cancel</Button>
            <Button onClick={saveModule} disabled={actionLoading} className="bg-[#0a2942] hover:bg-[#0d3a5c] text-white" data-testid="save-module-btn">
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Add Module
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lesson Dialog */}
      <Dialog open={lessonDialog} onOpenChange={setLessonDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Add Lesson</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Lesson Title *</Label><Input value={lessonForm.title} onChange={e => setLessonForm({...lessonForm, title: e.target.value})} /></div>
            <div>
              <Label>Type</Label>
              <Select value={lessonForm.type} onValueChange={v => setLessonForm({...lessonForm, type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="quiz">Quiz</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {lessonForm.type === 'video' && (
              <div><Label>Video URL</Label><Input value={lessonForm.video_url} onChange={e => setLessonForm({...lessonForm, video_url: e.target.value})} placeholder="YouTube or direct video URL" /></div>
            )}
            <div><Label>Content</Label><Textarea value={lessonForm.content} onChange={e => setLessonForm({...lessonForm, content: e.target.value})} rows={4} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Duration (minutes)</Label><Input type="number" value={lessonForm.duration_minutes} onChange={e => setLessonForm({...lessonForm, duration_minutes: parseInt(e.target.value) || 10})} /></div>
              <div><Label>Order</Label><Input type="number" value={lessonForm.order} onChange={e => setLessonForm({...lessonForm, order: parseInt(e.target.value) || 1})} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLessonDialog(false)}>Cancel</Button>
            <Button onClick={saveLesson} disabled={actionLoading} className="bg-[#0a2942] hover:bg-[#0d3a5c] text-white">
              {actionLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Add Lesson
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
